/**
 * useContractsHealth — thin fetch adapter over GET /api/contracts/health,
 * used by BacktestBanner. Re-fetches whenever `siteId` changes (the guard is
 * site-scoped, plan §4.9) and never resolves a stale site's answer into a
 * newer one (the `cancelled` guard — same lesson as InsightsApp/EregApp's
 * entitlement-check effects).
 */
import { useEffect, useState } from 'react';
import { getContractsHealth, ContractsHealth } from '../api/client';

export interface UseContractsHealthResult {
  health: ContractsHealth | null;
  loading: boolean;
  error: string | null;
}

export function useContractsHealth(siteId: string | null): UseContractsHealthResult {
  const [health, setHealth] = useState<ContractsHealth | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) {
      // No site selected — nothing to check yet; stay in the "not proven
      // passed" default (health null) rather than fetching without the
      // X-Site-Id the guard requires.
      setHealth(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getContractsHealth()
      .then((data) => {
        if (cancelled) return;
        setHealth(data);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error checking contracts module health:', err);
        // G13: fail loud, but toward the caller — health stays null (not the
        // last-known-good value) so a banner reading it defaults to "shown".
        setHealth(null);
        setError(err?.response?.data?.error || 'Failed to reach the contracts module');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [siteId]);

  return { health, loading, error };
}

export default useContractsHealth;
